import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import ReactDOM from 'react-dom';
import { Globe, ChevronDown, Lock, Unlock, Info, RotateCcw, X, List, Map as MapIcon, Search, Building2, Waves, Check } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// --- Types & Data ---
export interface LocationOverride {
  regionId: string;
  regionName: string;
  regionType: 'Country' | 'Market' | 'City' | 'Offshore';
}
interface Region {
  id: string;
  name: string;
  type: 'Country' | 'Market' | 'City' | 'Offshore';
  parentId?: string;
}
const REGIONS: Region[] = [
// Markets
{
  id: 'nordic',
  name: 'Nordic',
  type: 'Market'
}, {
  id: 'iberia',
  name: 'Iberian Peninsula',
  type: 'Market'
}, {
  id: 'cee',
  name: 'Central Eastern Europe',
  type: 'Market'
}, {
  id: 'balkans',
  name: 'Balkans',
  type: 'Market'
},
// Countries
{
  id: 'de',
  name: 'Germany',
  type: 'Country'
}, {
  id: 'uk',
  name: 'United Kingdom',
  type: 'Country'
}, {
  id: 'fr',
  name: 'France',
  type: 'Country'
}, {
  id: 'es',
  name: 'Spain',
  type: 'Country'
}, {
  id: 'it',
  name: 'Italy',
  type: 'Country'
}, {
  id: 'nl',
  name: 'Netherlands',
  type: 'Country'
}, {
  id: 'pl',
  name: 'Poland',
  type: 'Country'
}, {
  id: 'se',
  name: 'Sweden',
  type: 'Country'
}, {
  id: 'no',
  name: 'Norway',
  type: 'Country'
},
// Cities
{
  id: 'berlin',
  name: 'Berlin',
  type: 'City',
  parentId: 'de'
}, {
  id: 'london',
  name: 'London',
  type: 'City',
  parentId: 'uk'
}, {
  id: 'paris',
  name: 'Paris',
  type: 'City',
  parentId: 'fr'
}, {
  id: 'madrid',
  name: 'Madrid',
  type: 'City',
  parentId: 'es'
}, {
  id: 'rome',
  name: 'Rome',
  type: 'City',
  parentId: 'it'
}, {
  id: 'amsterdam',
  name: 'Amsterdam',
  type: 'City',
  parentId: 'nl'
}, {
  id: 'warsaw',
  name: 'Warsaw',
  type: 'City',
  parentId: 'pl'
}, {
  id: 'stockholm',
  name: 'Stockholm',
  type: 'City',
  parentId: 'se'
}, {
  id: 'oslo',
  name: 'Oslo',
  type: 'City',
  parentId: 'no'
}, {
  id: 'munich',
  name: 'Munich',
  type: 'City',
  parentId: 'de'
}, {
  id: 'hamburg',
  name: 'Hamburg',
  type: 'City',
  parentId: 'de'
},
// Offshore
{
  id: 'north_sea',
  name: 'North Sea',
  type: 'Offshore'
}, {
  id: 'baltic_sea',
  name: 'Baltic Sea',
  type: 'Offshore'
}, {
  id: 'mediterranean',
  name: 'Mediterranean',
  type: 'Offshore'
}];
// Coordinates for each region (for map markers)
const REGION_COORDS: Record<string, {
  lat: number;
  lng: number;
}> = {
  // Markets
  nordic: {
    lat: 62.0,
    lng: 15.0
  },
  iberia: {
    lat: 39.5,
    lng: -3.0
  },
  cee: {
    lat: 50.0,
    lng: 18.0
  },
  balkans: {
    lat: 42.5,
    lng: 21.0
  },
  // Countries
  de: {
    lat: 51.2,
    lng: 10.4
  },
  uk: {
    lat: 54.0,
    lng: -2.0
  },
  fr: {
    lat: 46.6,
    lng: 2.2
  },
  es: {
    lat: 40.0,
    lng: -3.7
  },
  it: {
    lat: 42.5,
    lng: 12.5
  },
  nl: {
    lat: 52.1,
    lng: 5.3
  },
  pl: {
    lat: 52.0,
    lng: 19.4
  },
  se: {
    lat: 62.0,
    lng: 15.0
  },
  no: {
    lat: 64.0,
    lng: 12.0
  },
  // Cities
  berlin: {
    lat: 52.52,
    lng: 13.4
  },
  london: {
    lat: 51.5,
    lng: -0.12
  },
  paris: {
    lat: 48.86,
    lng: 2.35
  },
  madrid: {
    lat: 40.42,
    lng: -3.7
  },
  rome: {
    lat: 41.9,
    lng: 12.5
  },
  amsterdam: {
    lat: 52.37,
    lng: 4.9
  },
  warsaw: {
    lat: 52.23,
    lng: 21.0
  },
  stockholm: {
    lat: 59.33,
    lng: 18.07
  },
  oslo: {
    lat: 59.91,
    lng: 10.75
  },
  munich: {
    lat: 48.14,
    lng: 11.58
  },
  hamburg: {
    lat: 53.55,
    lng: 9.99
  },
  // Offshore
  north_sea: {
    lat: 56.0,
    lng: 3.0
  },
  baltic_sea: {
    lat: 58.0,
    lng: 20.0
  },
  mediterranean: {
    lat: 38.0,
    lng: 15.0
  }
};
const TYPE_COLORS: Record<string, string> = {
  Country: '#10b981',
  Market: '#3b82f6',
  City: '#f59e0b',
  Offshore: '#06b6d4'
};
function createRegionIcon(type: string, isSelected: boolean): L.DivIcon {
  const color = isSelected ? '#EC4899' : TYPE_COLORS[type] || '#94a3b8';
  const size = type === 'City' ? 8 : type === 'Market' ? 14 : 10;
  const borderWidth = isSelected ? 2.5 : 1.5;
  const shadow = isSelected ? `0 0 8px ${color}80, 0 0 16px ${color}40` : `0 1px 4px rgba(0,0,0,0.5)`;
  const html = `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${borderWidth}px solid ${isSelected ? '#fff' : color + '80'};box-shadow:${shadow};transition:all 0.2s;"></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function MapFlyController({
  target
}: {
  target: {
    lat: number;
    lng: number;
  } | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 6, {
        duration: 0.8
      });
    }
  }, [target, map]);
  return null;
}
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}
interface LocationSelectorProps {
  onOverrideChange?: (override: LocationOverride | null) => void;
}
export function LocationSelector({
  onOverrideChange
}: LocationSelectorProps) {
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Region | null>(null);
  // Tab & Search state
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Auto-focus search when opening list tab
  useEffect(() => {
    if (open && activeTab === 'list' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, activeTab]);
  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Filtering logic
  const filteredRegions = useMemo(() => {
    if (!search) return REGIONS;
    const lower = search.toLowerCase();
    return REGIONS.filter(r => r.name.toLowerCase().includes(lower));
  }, [search]);
  const groupedRegions = useMemo(() => {
    const groups: Record<string, Region[]> = {};
    filteredRegions.forEach(r => {
      if (!groups[r.type]) groups[r.type] = [];
      groups[r.type].push(r);
    });
    return groups;
  }, [filteredRegions]);
  // Override Logic
  const buildOverride = (region: Region): LocationOverride => {
    return {
      regionId: region.id,
      regionName: region.name,
      regionType: region.type
    };
  };
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedLocation) {
      onOverrideChange?.(buildOverride(selectedLocation));
    } else {
      onOverrideChange?.(null);
    }
  };
  // Notify parent when selection changes while override is active
  useEffect(() => {
    if (overrideActive && selectedLocation) {
      onOverrideChange?.(buildOverride(selectedLocation));
    } else if (overrideActive && !selectedLocation) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  }, [selectedLocation, overrideActive, onOverrideChange]);
  const resetSelection = () => {
    setSelectedLocation(null);
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  // Helpers
  const getIconForType = (type: string) => {
    switch (type) {
      case 'Market':
        return <Globe size={12} className="text-blue-400" data-id="element-1385" />;
      case 'Country':
        return <Globe size={12} className="text-emerald-400" data-id="element-1386" />;
      case 'City':
        return <Building2 size={12} className="text-amber-400" data-id="element-1387" />;
      case 'Offshore':
        return <Waves size={12} className="text-cyan-400" data-id="element-1388" />;
      default:
        return <Globe size={12} data-id="element-1389" />;
    }
  };
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      left: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left
    };
  };
  const dropdownStyle: React.CSSProperties = {
    background: 'rgba(11, 17, 32, 0.97)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  // Simple SVG Map Component — REPLACED with Leaflet
  const InteractiveMap = () => {
    const visibleRegions = useMemo(() => {
      // Show countries and markets on the map (not cities by default to avoid clutter)
      // But show cities if one is selected
      return REGIONS.filter(r => {
        if (r.type === 'Country' || r.type === 'Market' || r.type === 'Offshore') return true;
        if (r.type === 'City' && selectedLocation?.type === 'City') return true;
        if (r.type === 'City' && selectedLocation?.id === r.id) return true;
        return false;
      }).filter(r => REGION_COORDS[r.id]);
    }, [selectedLocation]);
    const flyTarget = useMemo(() => {
      if (selectedLocation && REGION_COORDS[selectedLocation.id]) {
        return REGION_COORDS[selectedLocation.id];
      }
      return null;
    }, [selectedLocation]);
    return <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-white/[0.06]" data-id="element-1390">
        <MapContainer center={[50, 10]} zoom={4} className="h-full w-full" zoomControl={false} attributionControl={false} style={{
        background: '#0F172A'
      }} data-id="element-1391">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-1392" />
          <MapInvalidator data-id="element-1393" />
          <MapFlyController target={flyTarget} data-id="element-1394" />
          {visibleRegions.map(region => {
          const coords = REGION_COORDS[region.id];
          if (!coords) return null;
          const isSelected = selectedLocation?.id === region.id;
          return <Marker key={region.id} position={[coords.lat, coords.lng]} icon={createRegionIcon(region.type, isSelected)} eventHandlers={{
            click: () => setSelectedLocation(region)
          }} data-id="element-1395" />;
        })}
        </MapContainer>

        {/* Legend */}
        <div className="absolute bottom-2 left-2 flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-[9px] border border-white/[0.06]" style={{
        background: 'rgba(11, 17, 32, 0.85)',
        backdropFilter: 'blur(12px)'
      }} data-id="element-1396">
          {Object.entries(TYPE_COLORS).map(([type, color]) => <div key={type} className="flex items-center gap-1" data-id="element-1397">
              <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: color
          }} data-id="element-1398" />
              <span className="text-white/50" data-id="element-1399">{type}</span>
            </div>)}
        </div>

        {/* Selected indicator */}
        {selectedLocation && <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium text-[#EC4899] border border-[#EC4899]/20" style={{
        background: 'rgba(11, 17, 32, 0.85)',
        backdropFilter: 'blur(12px)'
      }} data-id="element-1400">
            <Check size={10} data-id="element-1401" />
            {selectedLocation.name}
          </div>}
      </div>;
  };
  return <>
      {/* Trigger Button */}
      <button ref={triggerRef} onClick={() => setOpen(!open)} className={`jua-toolbar-btn flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${overrideActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : open ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-1402">
        {overrideActive ? <Lock size={12} className="text-amber-400" data-id="element-1403" /> : <Globe size={12} className={open ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-1404" />}
        <span className="font-bold" data-id="element-1405">
          {overrideActive && selectedLocation ? selectedLocation.name : selectedLocation ? selectedLocation.name : 'Region Override'}
        </span>
        {overrideActive && <>
            <span className="text-amber-500/40" data-id="element-1406">·</span>
            <span className="text-amber-400/70 text-[10px]" data-id="element-1407">Override</span>
          </>}
        {selectedLocation && !overrideActive && <>
            <span className="text-slate-600" data-id="element-1408">·</span>
            <span className="text-slate-500" data-id="element-1409">{selectedLocation.type}</span>
          </>}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''} ${overrideActive ? 'text-amber-500' : open ? 'text-[#EC4899]' : 'text-slate-500'}`} data-id="element-1410" />
      </button>

      {/* Dropdown Panel */}
      {open && ReactDOM.createPortal(<div ref={panelRef} className="jua-location-panel fixed rounded-xl shadow-2xl shadow-black/60 border border-white/[0.08] overflow-hidden z-[9999] flex flex-col" style={{
      ...dropdownStyle,
      top: getPanelPosition().top,
      left: getPanelPosition().left,
      width: 420,
      maxHeight: 520
    }} data-id="element-1411">
            {/* Inner glow */}
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
      }} data-id="element-1412" />

            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/[0.06]" data-id="element-1413">
              <div className="flex items-center gap-2" data-id="element-1414">
                <h3 className="text-sm font-semibold text-white" data-id="element-1415">
                  Set overall workspace location
                </h3>
                <Info size={13} className="text-slate-500" data-id="element-1416" />
              </div>
              <div className="flex items-center gap-2" data-id="element-1417">
                <button onClick={resetSelection} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-1418">
                  <RotateCcw size={11} data-id="element-1419" /> Reset
                </button>
                <button onClick={() => setOpen(false)} className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/[0.06] transition-colors" data-id="element-1420">
                  <X size={14} data-id="element-1421" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="relative flex items-center border-b border-white/[0.06] px-2 pt-2" data-id="element-1422">
              <button onClick={() => setActiveTab('list')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'list' ? 'bg-white/[0.06] text-white border-t border-x border-white/[0.08]' : 'text-white/50 hover:text-white/80'}`} data-id="element-1423">
                <List size={12} data-id="element-1424" /> List View
              </button>
              <button onClick={() => setActiveTab('map')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'map' ? 'bg-white/[0.06] text-white border-t border-x border-white/[0.08]' : 'text-white/50 hover:text-white/80'}`} data-id="element-1425">
                <MapIcon size={12} data-id="element-1426" /> Map Selection
              </button>
            </div>

            {/* Content Area */}
            <div className="relative flex flex-col flex-1 overflow-hidden bg-white/[0.02] min-h-[300px]" data-id="element-1427">
              {activeTab === 'list' ? <>
                  {/* Search */}
                  <div className="p-3 border-b border-white/[0.06]" data-id="element-1428">
                    <div className="relative" data-id="element-1429">
                      <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-1430" />
                      <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cities, countries, or regions..." className="jua-location-search w-full h-8 pl-8 pr-3 rounded-lg bg-black/20 border border-white/[0.08] text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 transition-all" data-id="element-1431" />
                    </div>
                  </div>

                  {/* List */}
                  <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-4" data-id="element-1432">
                    {Object.keys(groupedRegions).length === 0 ? <div className="py-8 text-center text-xs text-white/30" data-id="element-1433">
                        No regions found matching "{search}"
                      </div> : ['Market', 'Country', 'City', 'Offshore'].map(type => {
              const regions = groupedRegions[type];
              if (!regions?.length) return null;
              return <div key={type} data-id="element-1434">
                            <div className="jua-location-section-header text-[9px] font-bold text-white/30 uppercase tracking-widest sticky top-0 z-10 -mx-2 px-4 py-1.5 border-b border-white/[0.04]" style={{
                  background: 'rgba(11, 17, 32, 1)',
                  backdropFilter: 'blur(8px)'
                }} data-id="element-1435">
                              {type === 'Market' ? 'Market Regions' : type === 'Offshore' ? 'Offshore Zones' : type === 'Country' ? 'Countries' : 'Cities'}
                            </div>
                            <div className="grid grid-cols-1 gap-0.5 mt-0.5 mb-3" data-id="element-1436">
                              {regions.map(region => <button key={region.id} onClick={() => setSelectedLocation(region)} className={`group flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${selectedLocation?.id === region.id ? 'bg-[#EC4899]/10 text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'}`} data-id="element-1437">
                                  <div className="flex items-center gap-2.5" data-id="element-1438">
                                    {getIconForType(region.type)}
                                    <span className="text-xs font-medium" data-id="element-1439">
                                      {region.name}
                                    </span>
                                    {region.parentId && <span className="text-[10px] text-white/30 group-hover:text-white/50" data-id="element-1440">
                                        {REGIONS.find(r => r.id === region.parentId)?.name}
                                      </span>}
                                  </div>
                                  {selectedLocation?.id === region.id && <Check size={12} className="text-[#EC4899]" data-id="element-1441" />}
                                </button>)}
                            </div>
                          </div>;
            })}
                  </div>
                </> : <div className="p-3 flex flex-col items-center justify-center h-full gap-3" data-id="element-1442">
                  <InteractiveMap data-id="element-1443" />
                  <p className="text-[10px] text-center text-white/40 max-w-[300px]" data-id="element-1444">
                    Click a marker to select a region. Countries, markets, and
                    offshore zones are shown.
                  </p>
                </div>}
            </div>

            {/* Footer */}
            <div className="relative px-4 py-2.5 flex items-center justify-between text-[11px] border-t border-white/[0.06]" data-id="element-1445">
              <div className="flex items-center gap-1.5" data-id="element-1446">
                <span className="text-slate-400" data-id="element-1447">Selected:</span>
                <span className={`font-bold ${selectedLocation ? 'text-white' : 'text-slate-500'}`} data-id="element-1448">
                  {selectedLocation ? selectedLocation.name : 'None'}
                </span>
              </div>

              {/* Override Toggle */}
              <button onClick={toggleOverride} disabled={!selectedLocation} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${overrideActive ? 'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25' : !selectedLocation ? 'bg-white/[0.02] border-white/[0.04] text-slate-600 cursor-not-allowed' : 'bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/[0.08]'}`} title={overrideActive ? 'Disable override — widgets revert to their own location' : 'Override all widgets with this location'} data-id="element-1449">
                {overrideActive ? <Lock size={10} data-id="element-1450" /> : <Unlock size={10} data-id="element-1451" />}
                {overrideActive ? 'Override Active' : 'Override All Widgets'}
              </button>
            </div>
          </div>, document.body)}
    </>;
}